import { freezeStyleSheet } from './resolveStyles';
import type { JsonStyleSheet } from './resolveStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A frozen, resolved single-file style registry (one per *.styles.json source). */
export interface StyleObjectInstance {
  /**
   * Inline mode — data is provided directly.
   * The data is deep-frozen and stored under `name`.
   * Calling with the same name twice is idempotent (first write wins).
   */
  registerStyleObject(name: string, data: JsonStyleSheet): Readonly<JsonStyleSheet>;

  /**
   * Extraction mode — data lives in a pre-loaded styles bundle.
   * `fileBundleKey` is the per-file key stored by `loadBundleMap()`:
   *   `"${appId}:${relFilePath}"` e.g. `"streamlog:src/native/screens/StreamLogScreen"`
   * `name` is the export name within that file's style map.
   */
  registerStyleObject(name: string, fileBundleKey: string): Readonly<JsonStyleSheet>;

  /**
   * Register all entries from a map at once (inline mode).
   * Returns a frozen map of frozen style objects.
   */
  registerStyleObjectMap(
    map: Record<string, JsonStyleSheet>,
  ): Readonly<Record<string, Readonly<JsonStyleSheet>>>;

  /**
   * Extraction mode variant — loads the entire file-scoped bundle.
   * `fileBundleKey` must have been loaded via `styleBundleUtil.loadBundleMap()`.
   */
  registerStyleObjectMap(
    fileBundleKey: string,
  ): Readonly<Record<string, Readonly<JsonStyleSheet>>>;
}

// ─── Bundle registry ──────────────────────────────────────────────────────────

// Keys are per-file bundle keys: `"${appId}:${relFilePath}"`.
// Populated by styleBundleUtil.loadBundleMap() before the mini-app renders.
const _loadedBundles = new Map<string, Record<string, JsonStyleSheet>>();

// ─── Instance factory ─────────────────────────────────────────────────────────

function createInstance(): StyleObjectInstance {
  const registry = new Map<string, Readonly<JsonStyleSheet>>();

  function registerStyleObject(
    name: string,
    dataOrKey: JsonStyleSheet | string,
  ): Readonly<JsonStyleSheet> {
    if (registry.has(name)) return registry.get(name)!;

    let sheet: JsonStyleSheet;
    if (typeof dataOrKey === 'string') {
      // ── Extraction mode ───────────────────────────────────────────────────
      const fileBundle = _loadedBundles.get(dataOrKey);
      if (!fileBundle || !(name in fileBundle)) {
        throw new Error(
          `[styleBundleUtil] Style "${name}" not found in bundle "${dataOrKey}". ` +
          `Call styleBundleUtil.loadBundleMap(appId, data) before mounting.`,
        );
      }
      sheet = fileBundle[name];
    } else {
      // ── Inline mode ───────────────────────────────────────────────────────
      sheet = dataOrKey;
    }

    const frozen = freezeStyleSheet(sheet);
    registry.set(name, frozen);
    return frozen;
  }

  function registerStyleObjectMap(
    mapOrKey: Record<string, JsonStyleSheet> | string,
  ): Readonly<Record<string, Readonly<JsonStyleSheet>>> {
    let map: Record<string, JsonStyleSheet>;

    if (typeof mapOrKey === 'string') {
      // ── Extraction mode ───────────────────────────────────────────────────
      const fileBundle = _loadedBundles.get(mapOrKey);
      if (!fileBundle) {
        throw new Error(
          `[styleBundleUtil] Bundle "${mapOrKey}" not loaded. ` +
          `Call styleBundleUtil.loadBundleMap(appId, data) before mounting.`,
        );
      }
      map = fileBundle;
    } else {
      map = mapOrKey;
    }

    const result: Record<string, Readonly<JsonStyleSheet>> = {};
    for (const name of Object.keys(map)) {
      result[name] = registerStyleObject(name, map[name]);
    }
    return Object.freeze(result);
  }

  return { registerStyleObject, registerStyleObjectMap };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const styleBundleUtil = {
  /**
   * Returns a fresh StyleObjectInstance for one styles source file.
   * The plugin generates exactly one call per *.styles.json file.
   */
  getModuleInstance(): StyleObjectInstance {
    return createInstance();
  },

  /**
   * Load an extracted styles bundle for a mini-app.
   *
   * `data` is the parsed content of `ui.rn.styles.json`:
   * ```json
   * {
   *   "src/native/screens/StreamLogScreen": {
   *     "streamLogStylesJson": { ... },
   *     "dataRowStylesJson":   { ... }
   *   }
   * }
   * ```
   * Internally stored under per-file keys `"${appId}:${relFilePath}"` so different
   * apps' style data never collide in the registry.
   *
   * Call this BEFORE eval'ing the mini-app bundle.
   *
   * @param appId  Mini-app identifier (e.g. `"streamlog"`)
   * @param data   Parsed `ui.rn.styles.json` content
   */
  loadBundleMap(
    appId: string,
    data: Record<string, Record<string, JsonStyleSheet>>,
  ): void {
    for (const [relFilePath, fileStyles] of Object.entries(data)) {
      _loadedBundles.set(`${appId}:${relFilePath}`, fileStyles);
    }
  },

  /**
   * Release all style data for a mini-app, making it GC-eligible.
   * Call when the mini-app is unloaded from `global.__miniApps`.
   */
  unloadApp(appId: string): void {
    const prefix = `${appId}:`;
    for (const key of _loadedBundles.keys()) {
      if (key.startsWith(prefix)) _loadedBundles.delete(key);
    }
  },
};

